<script setup lang="ts">
import { useField } from 'vee-validate'
import { isNullOrUndefined } from '@/utils'

const props = defineProps({
  type: {
    type: String,
    default: 'text',
  },
  name: {
    type: String,
    required: true,
  },
  required: {
    type: Boolean,
    default: false,
  },
  multiline: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  label: {
    type: String,
    default: '',
  },
  autocomplete: {
    type: String,
    default: 'off',
  },
  placeholder: {
    type: String,
    default: '',
  },
  append: {
    type: String,
    default: '',
  },
  labelClass: {
    type: String,
    default: '',
  },
  cssClass: {
    type: String,
    default: '',
  },
  min: {
    type: Number,
    default: 0,
  },
  validation: {
    type: Boolean,
    default: true,
  },
  borderDanger: {
    type: Boolean,
    default: false,
  },
  unstyled: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits<{
  (e: 'change', value: number | string): void
  (e: 'blur'): void
}>()

const nameRef = toRef(props, 'name')

const {
  value: inputValue,
  errorMessage,
  handleBlur,
  handleChange,
  setValue: setFieldValue,
  resetField,
} = useField<string | number | null | undefined>(nameRef)

const hasValue = ref(false)

const reset = () => resetField({ value: '' })
const setValue = (value: string) => setFieldValue(value)

defineExpose({
  reset, setValue,
})

function onInput(e?: Event) {
  handleChange(e)
  hasTextOrNumber(inputValue.value)

  const emittedValue = props.type === 'number' ? Number(inputValue.value) : inputValue.value

  emit('change', emittedValue as string | number)
}

function hasTextOrNumber(inputData: unknown) {
  if (isNullOrUndefined(inputData)) {
    hasValue.value = false

    return
  }
  if (typeof inputData === 'string') {
    hasValue.value = inputData.trim() !== ''

    return
  }
  if (typeof inputData === 'number') {
    hasValue.value = !Number.isNaN(inputData)

    return
  }
  hasValue.value = true
}
</script>

<template>
  <div :class="unstyled ? '' : ['validation-input', { 'has-error': !!errorMessage }]">
    <label
      v-if="label" class="form-label d-block"
      :class="[labelClass, { required }]" :for="name"
    >{{ label }}</label>
    <textarea
      v-if="multiline" :id="name"
      :class="[unstyled ? '' : 'form-control d-block user-input', cssClass]"
      :name="name" :disabled="disabled"
      :value="inputValue" :placeholder="placeholder"
      @input="onInput" @blur="handleBlur"
    />
    <div v-else :class="unstyled ? '' : 'input-group'">
      <input
        :class="[
          unstyled ? '' : 'form-control d-block user-input',
          cssClass,
          { 'border-danger': !hasValue && borderDanger && !disabled },
        ]"
        :name="name" :min="min"
        :disabled="disabled"
        :type="type" :value="inputValue"
        :placeholder="placeholder" :autocomplete="autocomplete"
        @input="onInput" @blur="handleBlur"
      >
      <span v-if="append" class="input-group-text bg-light">{{ append }}</span>
    </div>

    <ValidationMessage v-if="validation" :error-message="errorMessage" :unstyled="unstyled" />
  </div>
</template>
